import { DealSummaryListDto } from '~/models/deals/DealsListItem.dto';
import React, { useEffect, useMemo, useState } from 'react';
import { DateRange } from '@material-ui/pickers-next/DateRangePicker/RangeTypes';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { useSelector } from 'react-redux';
import { selectDetectSongChange } from '~/features/dashboard/store/selectors';
import moment from 'moment';
import { axiosInstance } from '~/api/config/axios.config';
import { detectSongChange } from '~/features/dashboard/store/actions';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Box, Button, Grid, TextField, Typography } from '@material-ui/core';
import css from '~/features/dashboard/components/song-details/styles.module.scss';
import { ChipsFilter } from '~/common/chips-filter';
import { Search } from '~/common/search';
import { DateRangeDelimiter, DateRangePicker, LocalizationProvider } from '@material-ui/pickers-next';
import MomentUtils from '@material-ui/pickers-next/adapter/moment';
import { SongDealCard } from '~/features/dashboard/components/song-deal-card';
import { PaginatedBlock } from '~/features/dashboard/components/paginated-block';
import { useDateFormat } from '~/common/date-format';

interface DealsGridProps {
    deals: DealSummaryListDto[];
    projectId: any;
    id: any;
    showDeal: string;
    changeToggleDrawer: (event: any) => void;
    onLinkDeal: (data: any, type: any) => void;
    onLinkingDeal: (data: any, type: any) => void;
    drawerTitle: any;
}

export const DealsTab: React.FC<DealsGridProps> = ({ deals, projectId, id, changeToggleDrawer, onLinkDeal, showDeal, drawerTitle, onLinkingDeal }) => {
    const { selectedDateFormat } = useDateFormat();
    const [filteredDeals, setFilteredDeals] = useState<DealSummaryListDto[]>([]);
    const [dealTypesFilter, setDealTypesFilter] = useState<string[]>([]);
    const [dealStatusFilter, setDealStatusFilter] = useState<string[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [dateFilter, setDateFilter] = React.useState<DateRange<Date>>([null, null]);
    const dispatch = useThunkDispatch();

    let songChange = useSelector(selectDetectSongChange);

    const types = useMemo(() => {
        const dealTypes: string[] = [];
        for (const deal of deals) {
            if (deal.dealType.trim() && !dealTypes.includes(deal.dealType)) {
                dealTypes.push(deal.dealType);
            }
        }
        return dealTypes;
    }, [deals]);

    const statuses = useMemo(() => {
        const dealStatuses: string[] = [];
        for (const deal of deals) {
            if (deal && deal.contractType && deal.contractType.trim() && !dealStatuses.includes(deal.contractType)) {
                dealStatuses.push(deal.contractType);
            }
        }
        return dealStatuses;
    }, [deals]);

    useEffect(() => {
        const typeFilteredDeals = deals.filter(deal => (dealTypesFilter.length ? dealTypesFilter.includes(deal.dealType) : true));
        const statusFilteredDeals = typeFilteredDeals.filter(deal => (dealStatusFilter.length ? dealStatusFilter.includes(deal.contractType) : true));
        const searchFilteredDeals = statusFilteredDeals.filter(
            deal => deal.contractTitle.toLowerCase().includes(searchValue.toLowerCase()) || deal.contractTitle.toLowerCase().includes(searchValue.toLowerCase())
        );
        const [minDate, maxDate] = dateFilter;
        const dateFilteredDeals = searchFilteredDeals.filter(deal => {
            const compareDate = moment(deal.contractStartDate);
            return !(compareDate.isBefore(minDate) || compareDate.isAfter(maxDate));
        });
        setFilteredDeals(dateFilteredDeals);
    }, [searchValue, deals, dealTypesFilter, dateFilter, dealStatusFilter]);

    const linkAll = (projectId: any, assetCode: any) => {
        axiosInstance
            .post(`catalogue/projectCreator/asset/link?projectid=${projectId}&songRef=${assetCode}&all=true`)
            .then(() => {
                onLinkingDeal(filteredDeals[0], 'link');
            })
            .finally(() => {
                dispatch(detectSongChange({ value: true }));
                // notifySuccess({
                //     title: 'Success',
                //     message: `All Deals Link successfully`,
                // });
            });
    };

    const filteredItems = filteredDeals.filter(t => (drawerTitle === 'ADD DEALS' ? t.dealSongLinkFlag !== 1 : t.dealSongLinkFlag === 1));

    return (
        <div className="">
            <div className="" style={{ display: 'flex', borderBottom: '1px solid #ccc', padding: '10px 0px 10px 24px', verticalAlign: 'center' }}>
                <ArrowBackIcon style={{ verticalAlign: 'center', lineHeight: '10px', cursor: 'pointer' }} onClick={() => changeToggleDrawer(false)} />
                <Typography
                    variant={'h6'}
                    className={css.drawertitle}
                    onClick={() => changeToggleDrawer(false)}
                    style={{ lineHeight: '25px', cursor: 'pointer', marginLeft: '0.5rem' }}
                >
                    Close
                </Typography>
            </div>
            <Grid container className={css.gridDeals} spacing={3}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant={'h6'} className={css.drawertitle}>
                            {drawerTitle}
                        </Typography>
                        {drawerTitle === 'ADD DEALS' && (
                            <Button
                                className={css.btnColorGreen}
                                variant="contained"
                                color="secondary"
                                onClick={() => linkAll(projectId, id)}
                                disabled={songChange}
                            >
                                Link All
                            </Button>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    {types.length > 1 && (
                        <Box pb={2}>
                            <Box pb={1}>
                                <Typography>Deal type filters:</Typography>
                            </Box>
                            <ChipsFilter className={css.chipsDeals} chips={types} filters={dealTypesFilter} onFilterChange={setDealTypesFilter} />
                        </Box>
                    )}
                    {statuses.length > 1 && (
                        <Box pb={2}>
                            <Box pb={1}>
                                <Typography>Deal status filters:</Typography>
                            </Box>
                            <ChipsFilter className={css.chipsDeals} chips={statuses} filters={dealStatusFilter} onFilterChange={setDealStatusFilter} />
                        </Box>
                    )}
                </Grid>
                <Grid item xs={6}>
                    <Search search={searchValue} onSearchChange={setSearchValue} className={css.search} />
                    <LocalizationProvider dateAdapter={MomentUtils}>
                        <DateRangePicker
                            startText="Start Date"
                            endText="End Date"
                            value={dateFilter}
                            inputFormat={selectedDateFormat}
                            onChange={newValue => setDateFilter(newValue)}
                            renderInput={(startProps, endProps) => (
                                <React.Fragment>
                                    <TextField {...startProps} variant="standard" />
                                    <DateRangeDelimiter> to </DateRangeDelimiter>
                                    <TextField {...endProps} variant="standard" />
                                </React.Fragment>
                            )}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                    <PaginatedBlock
                        grid={2}
                        keyName="assetCode"
                        data={filteredItems}
                        renderItem={item => (
                            <SongDealCard
                                key={item.assetCode}
                                deal={item}
                                projectId={projectId}
                                id={id}
                                onLinkDeal={onLinkDeal}
                            />
                        )}
                    />
                </Grid>
            </Grid>
        </div>
    );
};
